export function renderAbout(): string {
  return /* html */`
<div class="page-wrapper about-page">

  <div class="page-hero">
    <span class="section-label">About Xaebia</span>
    <h1 class="hero-title anim-fade-up">
      Software from the edge of<br/>
      <span class="gradient-text">the continent.</span>
    </h1>
    <p class="hero-mission anim-fade-up" style="animation-delay:0.15s">
      We're a small software company based in Santa Cruz, California.
      We build AI tools, web applications, open-source systems, and
      experimental interfaces — the kind of technology that makes you
      feel like the future arrived early.
    </p>
  </div>

  <div class="page-section">

    <div class="about-grid">
      <div class="about-text">
        <h2 class="section-title" style="font-size:var(--text-3xl)">What Xaebia is</h2>
        <div class="prose">
          <p>
            Xaebia started as a collection of obsessions: a terminal AI assistant
            that actually understood how developers work, a procedural audio engine
            that treated synthesis as software design, a chess engine that became
            a compiler exploration. Projects that refused to stay small.
          </p>
          <p>
            At some point the projects started pointing at something. A company —
            not the Silicon Valley kind, not the enterprise SaaS kind, but something
            stranger. Software built by people who care about what software is for.
          </p>
          <p>
            Santa Cruz isn't an accident. The ocean keeps you honest. You build things
            because they're worth building, not because a VC said so. We ship because
            the work is ready, not because a metric needed moving.
          </p>
          <p>
            We make apps, tools, games, experiments, and open-source libraries. Some
            of it is for developers. Some of it is for anyone who uses a computer and
            wants that experience to be better. All of it is real software, built with
            real care, by a small team that thinks deeply about what it builds.
          </p>
        </div>
      </div>

      <div class="about-facts">
        ${[
          { label: 'Location', value: 'Santa Cruz, CA', sub: '30 miles from Silicon Valley, light years from Silicon Valley culture' },
          { label: 'Founded', value: '2024' },
          { label: 'Team', value: 'Small & growing', sub: 'Remote-friendly, California-based' },
          { label: 'Model', value: 'Independent', sub: 'No VC funding. Building sustainably.' },
        ].map(f => `
          <div class="fact-card card">
            <div class="fact-label">${f.label}</div>
            <div class="fact-value">${f.value}</div>
            ${f.sub ? `<div class="fact-sub">${f.sub}</div>` : ''}
          </div>
        `).join('')}
      </div>
    </div>

    <div class="divider"></div>

    <h2 class="section-title">What we believe</h2>
    <div class="beliefs-grid stagger">
      ${[
        { icon: '🔓', title: 'Open by default', body: 'Most of what we build is open source. GitHub is where our work lives publicly — not as a marketing exercise, but because we think software should be inspectable.' },
        { icon: '🏠', title: 'Local first', body: 'Applications that respect your data, work offline, and don\'t hold your files hostage in someone else\'s cloud. Your computer is powerful. Software should use it.' },
        { icon: '🌊', title: 'Built to last', body: 'We build for longevity, not just for launch. That means choosing boring stack components where appropriate, writing real documentation, and not abandoning projects without warning.' },
        { icon: '⚡', title: 'Speed is a feature', body: 'Fast software is respectful software. We take performance seriously from the beginning, not as a final polish step before release.' },
        { icon: '🔬', title: 'Experiment openly', body: 'Not every project is a product. Some are explorations. We ship experiments because they might be useful and because building them teaches things that eventually feed into real products.' },
        { icon: '🐙', title: 'No surveillance', body: 'We don\'t track users beyond what\'s necessary to provide the service. We won\'t sell your data. We don\'t want your data.' },
      ].map(b => `
        <div class="belief-card card">
          <div class="belief-icon">${b.icon}</div>
          <h3 class="belief-title">${b.title}</h3>
          <p class="belief-body">${b.body}</p>
        </div>
      `).join('')}
    </div>

    <div class="divider"></div>

    <!-- Tech stack -->
    <h2 class="section-title">How we build</h2>
    <p class="section-body" style="margin-bottom:var(--s8)">
      We're not dogmatic about stack choices, but we have strong defaults.
      Here's what you'll find across most Xaebia projects:
    </p>
    <div class="tech-grid">
      ${[
        { category: 'Languages', items: ['Python', 'TypeScript', 'Rust', 'JavaScript'] },
        { category: 'Frontend', items: ['Vanilla TS', 'Three.js', 'Vite', 'Web APIs'] },
        { category: 'Backend', items: ['FastAPI', 'Node.js', 'SQLite', 'Postgres'] },
        { category: 'Mobile', items: ['React Native', 'Swift', 'Kotlin'] },
        { category: 'AI / ML', items: ['Anthropic Claude', 'OpenAI', 'HuggingFace', 'GGML'] },
        { category: 'Infrastructure', items: ['Fly.io', 'Cloudflare', 'GitHub Actions'] },
      ].map(t => `
        <div class="tech-group card">
          <div class="tech-category">${t.category}</div>
          <div class="tech-items">
            ${t.items.map(i => `<span class="badge badge-dim">${i}</span>`).join('')}
          </div>
        </div>
      `).join('')}
    </div>

    <div class="divider"></div>

    <!-- CTA -->
    <div class="about-cta">
      <h2 class="section-title" style="font-size:var(--text-2xl)">Build something with us</h2>
      <p class="section-body">We're always open to collaborations that make sense.</p>
      <div class="hero-actions" style="margin-top:var(--s6)">
        <a href="#contact" class="btn btn-primary">Contact Xaebia</a>
        <a href="#projects" class="btn btn-secondary">See GitHub Work</a>
      </div>
    </div>

  </div>
</div>
`;
}
